import {
  AppTypeEnum,
  useCreateShippingZoneMutation,
  useInstalledAppsListQuery,
  usePluginsQuery,
} from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import useShop from "@dashboard/hooks/useShop";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Skeleton, Text } from "@saleor/macaw-ui-next";
import { Truck } from "lucide-react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import { EmptyState } from "../../components/EmptyState";
import {
  AppIframeView,
  ExtensionItem,
  ExtensionList,
  PluginConfigView,
} from "../../components/ExtensionConfig";
import { StepWrapper } from "../../components/StepWrapper";
import { useWizardContext } from "../../context/WizardContext";
import { messages } from "./messages";
import { shippingSchema } from "./schema";
import { SHIPPING_FORM_ID, ShippingForm } from "./ShippingForm";
import { ShippingFormValues } from "./types";

const SUPPORTED_SHIPPING_NAMES = [
  "freightcom",
  "fedex",
  "ups air freight",
  "ups shipping",
  "usps",
  "tforce",
];

const isSupportedShipping = (name: string | null | undefined): boolean => {
  if (!name) return false;

  const lower = name.toLowerCase();

  return SUPPORTED_SHIPPING_NAMES.some(supported =>
    lower.includes(supported),
  );
};

type SelectedExtension = {
  id: string;
  type: "app" | "plugin";
};

export const ShippingStep: React.FC = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const shop = useShop();
  const { completeCurrentStep, completeWizard } = useWizardContext();
  const [selected, setSelected] = useState<SelectedExtension | null>(null);

  const [createShippingZone, { loading: zoneLoading }] = useCreateShippingZoneMutation();

  const { data: appsData, loading: appsLoading } = useInstalledAppsListQuery({
    variables: {
      first: 100,
      filter: { type: AppTypeEnum.THIRDPARTY },
    },
  });

  const { data: pluginsData, loading: pluginsLoading } = usePluginsQuery({
    variables: { first: 100 },
  });

  const installedApps = mapEdgesToItems(appsData?.apps) ?? [];
  const shippingApps = installedApps.filter(app =>
    isSupportedShipping(app.name),
  );

  const allPlugins = mapEdgesToItems(pluginsData?.plugins) ?? [];
  const shippingPlugins = allPlugins.filter(plugin =>
    isSupportedShipping(plugin.name),
  );

  const extensionItems: ExtensionItem[] = [
    ...shippingApps.map(app => ({
      id: app.id,
      name: app.name ?? "",
      logoUrl: app.brand?.logo?.default ?? null,
      isActive: app.isActive ?? false,
      type: "app" as const,
    })),
    ...shippingPlugins.map(plugin => {
      const isActive =
        plugin.globalConfiguration?.active === true ||
        plugin.channelConfigurations?.some(c => c.active) === true;

      return {
        id: plugin.id,
        name: plugin.name,
        logoUrl: null,
        isActive,
        type: "plugin" as const,
      };
    }),
  ];

  const countryChoices = (shop?.countries ?? []).map(c => ({
    label: c.country,
    value: c.code,
  }));

  const { control, handleSubmit } = useForm<ShippingFormValues>({
    resolver: zodResolver(shippingSchema),
    defaultValues: {
      name: "",
      description: "",
      countries: [],
    },
  });

  const onSubmit = async (data: ShippingFormValues) => {
    const result = await createShippingZone({
      variables: {
        input: {
          name: data.name,
          description: data.description,
          countries: data.countries,
        },
      },
    });

    const errors = result.data?.shippingZoneCreate?.errors ?? [];

    if (errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(messages.successMessage),
      });
      completeCurrentStep();
      completeWizard();

      return;
    }

    notify({
      status: "error",
      text: errors[0]?.message ?? intl.formatMessage(messages.errorMessage),
    });
  };

  const extensionsLoading = appsLoading || pluginsLoading;

  return (
    <StepWrapper
      title={intl.formatMessage(messages.title)}
      description={intl.formatMessage(messages.description)}
      skippable={true}
      isLastStep={true}
      formId={SHIPPING_FORM_ID}
      isSaving={zoneLoading}
    >
      {selected ? (
        <>
          <form
            id={SHIPPING_FORM_ID}
            onSubmit={e => {
              e.preventDefault();
              completeCurrentStep();
              completeWizard();
            }}
          />
          {selected.type === "app" ? (
            <AppIframeView
              appId={selected.id}
              onBack={() => setSelected(null)}
            />
          ) : (
            <PluginConfigView
              pluginId={selected.id}
              onBack={() => setSelected(null)}
            />
          )}
        </>
      ) : (
        <>
          <ShippingForm
            control={control}
            countryChoices={countryChoices}
            disabled={zoneLoading}
            onSubmit={handleSubmit(onSubmit)}
          />

          <Box marginTop={6}>
            <Box display="flex" alignItems="center" gap={2} marginBottom={1}>
              <Text size={5} fontWeight="bold" as="h2">
                {intl.formatMessage(messages.shippingAppsTitle)}
              </Text>
            </Box>
            <Text size={3} color="default2" as="p" paddingBottom={4}>
              {intl.formatMessage(messages.shippingAppsDescription)}
            </Text>
            {extensionsLoading ? (
              <Box display="flex" flexDirection="column" gap={3}>
                <Skeleton __height="72px" borderRadius={4} />
                <Skeleton __height="72px" borderRadius={4} />
              </Box>
            ) : extensionItems.length === 0 ? (
              <EmptyState
                icon={Truck}
                message={intl.formatMessage(messages.noShippingApps)}
              />
            ) : (
              <ExtensionList
                extensions={extensionItems}
                onConfigure={item =>
                  setSelected({ id: item.id, type: item.type })
                }
                emptyMessage={intl.formatMessage(
                  messages.noShippingApps,
                )}
              />
            )}
          </Box>
        </>
      )}
    </StepWrapper>
  );
};
